import type { CalendarEventDto } from '@chat-emp/shared';

import { Icon } from '../components/Icon.js';
import { isOver } from './event-rules.js';
import { EventView } from './EventView.js';
import styles from './EventDetailsPanel.module.css';

interface EventDetailsPanelProps {
  event: CalendarEventDto;
  onClose: () => void;
  /** Add guests to it. Offered only while the event can still take them. */
  onEdit: (event: CalendarEventDto) => void;
}

/** The calendar refuses new guests once an event is over or closed. */
function canAddGuests(event: CalendarEventDto): boolean {
  return event.status !== 'completed' && !isOver(event);
}

/**
 * The frame around an event's full view — the Event section's third column, and
 * on a phone the full-screen sheet ChatLayout already lifts `.context` into.
 *
 * Read-only, bar the pencil: it opens the Chat Day popup to add guests (AI-3215).
 */
export function EventDetailsPanel({ event, onClose, onEdit }: EventDetailsPanelProps) {
  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        {/* The name lives here rather than in EventView, which keeps to the
            body: the header is where every other pane in this app puts one. */}
        <h2 className={styles.title}>{event.title}</h2>
        <span className={styles.actions}>
          {canAddGuests(event) && (
            <button
              type="button"
              className={styles.close}
              aria-label="Add guests"
              title="Add guests"
              onClick={() => onEdit(event)}
            >
              <Icon name="edit" size={18} />
            </button>
          )}
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </span>
      </header>
      <div className={styles.body}>
        <EventView event={event} />
      </div>
    </div>
  );
}
